import React from 'react';
import styled, { css } from 'styled-components';
import { NavLinkProps } from 'react-router-dom';

import { HudIcon, IconType } from '@hud/web-components/Icons/HudIcons';
import { smRegular, xsRegular } from '@hud/web-components/theme/typography';
import { themeColor } from '@hud/web-components/theme/colors';
import {
  clickableMixin,
  ellipsisMixin,
  transitionFunc,
} from '@hud/web-components/theme/utils';
import { NavLink } from '../Link/Link';

interface ItemProps extends React.PropsWithChildren {
  icon?: IconType;
  aside?: React.ReactNode;
  active?: boolean;
}

interface GroupProps extends React.PropsWithChildren {
  label?: string;
  initiallyClosed?: boolean;
}

function Item({
  icon,
  children,
  aside,
  active,
  ...rest
}: ItemProps & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <ItemContainer {...rest} className={active ? 'active' : ''}>
      {icon && <HudIcon icon={icon} size="20px" />}
      <ItemLabel>{children}</ItemLabel>
      {aside}
    </ItemContainer>
  );
}

type LinkProps = Omit<NavLinkProps, 'to'> & ItemProps & { to: string };

function Link({ to, icon, children, aside, ...rest }: LinkProps) {
  return (
    <LinkContainer to={to} {...rest}>
      {icon && <HudIcon icon={icon} size="20px" />}
      <ItemLabel>{children}</ItemLabel>
      {aside}
    </LinkContainer>
  );
}

function Group({ label, initiallyClosed, children, ...rest }: GroupProps) {
  const [isExpanded, setIsExpanded] = React.useState(!initiallyClosed);
  const toggle = () => setIsExpanded((prev) => !prev);

  return (
    <GroupContainer {...rest}>
      {label && (
        <GroupHeader onClick={toggle}>
          <GroupLabel>{label}</GroupLabel>
          <GroupIcon $isExpanded={isExpanded}>
            <HudIcon icon="menu.chevronDown" size="20px" />
          </GroupIcon>
        </GroupHeader>
      )}
      <GroupItems $isExpanded={isExpanded}>{children}</GroupItems>
    </GroupContainer>
  );
}

const itemStyle = css`
  ${smRegular};
  ${clickableMixin};

  overflow: hidden;
  transition: 200ms cubic-bezier(0.165, 0.84, 0.44, 1);

  padding: var(--space-100) var(--space-150);

  display: flex;
  align-items: center;
  gap: var(--space-100);

  border-radius: 8px;
  color: var(--text-standard-secondary);

  svg {
    position: relative;
    top: -1px;
    path {
      color: var(--text-standard-secondary);
    }
  }

  &:hover {
    background: var(--sidebar-item-hover);
  }

  &.active {
    color: var(--text-primary);
    background: var(--sidebar-item-selected);
    svg path {
      color: var(--text-primary);
    }

    &:hover {
      background: var(--sidebar-item-hover);
    }
  }
`;

const ItemContainer = styled.div`
  ${itemStyle};
`;

const LinkContainer = styled(NavLink)`
  ${itemStyle};
`;

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  /* GroupContainer next to GroupContainer should have padding */
  & + & {
    padding-top: var(--space-50)
  }
`;

const GroupItems = styled(GroupContainer)<{ $isExpanded: boolean }>`
  max-height: 0;
  overflow: hidden;
  transition: 0.3s ${transitionFunc};
  padding: 0; /* Ensure no padding when closed */
  margin-top: 0px;

  ${({ $isExpanded }) =>
    $isExpanded &&
    css`
      margin-top: 0;
      max-height: fit-content;
    `}
`;

const ItemLabel = styled.div`
  flex: 1;
  ${ellipsisMixin};
`;

const GroupHeader = styled.div`
  ${xsRegular};
  ${clickableMixin};
  padding: 5px 6px 5px 0px;
  color: ${themeColor('textSecondary')};

  display: flex;
  align-items: center;
`;

const GroupLabel = styled.div`
  flex: 1;
  color: var(--text-subtle);
`;

const GroupIcon = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;

  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '0deg' : '180deg')});
  transition: transform 0.25s ${transitionFunc};
`;

export const Navigation = {
  Item,
  Link,
  Group,
};
